import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, ChevronUp } from 'lucide-react';
import api from '../services/api';

interface FAQ {
  id: number;
  question: string;
  answer: string;
  category: string;
}

const staticFAQs: FAQ[] = [
  {
    id: 1,
    question: 'How long does a website redesign take?',
    answer: 'A typical website redesign takes 2-4 weeks depending on scope and complexity. We work closely with you throughout the process to ensure the final product matches your vision. Simple sites may be done in under 2 weeks, while complex projects with custom functionality may take longer.',
    category: 'Services'
  },
  {
    id: 2,
    question: 'What does the Basic SEO Fixup include?',
    answer: 'Our $1,000 Basic SEO Fixup includes a full site audit, on-page optimization (meta tags, headings, image alt text), keyword research and implementation, Google Search Console setup, sitemap creation, page speed optimization, and a detailed report of changes made. This is a one-time service designed to significantly improve your search rankings.',
    category: 'Services'
  },
  {
    id: 3,
    question: 'What is included in the Complete Web Redesign?',
    answer: 'Our $2,000 Complete Web Redesign includes a fully custom, modern responsive design, mobile optimization, SEO-friendly structure, up to 10 pages, contact forms, and basic analytics setup. We handle everything from design mockups to deployment. Additional pages or custom features can be added for an extra cost.',
    category: 'Services'
  },
  {
    id: 4,
    question: 'What does the Hosting & Management plan cover?',
    answer: 'For $20/month, our Hosting & Management service includes reliable web hosting, SSL certificate, regular security updates, daily backups, uptime monitoring, and minor content updates (text and image changes). This ensures your site stays fast, secure, and up-to-date without you having to worry about the technical details.',
    category: 'Services'
  },
  {
    id: 5,
    question: 'Do you offer payment plans?',
    answer: 'Yes, we understand that investing in your web presence is a big decision. We offer flexible payment plans for larger projects. Typically, we split payments into a 50% deposit upfront and 50% upon project completion. For custom app development, we can arrange milestone-based payments. Contact us to discuss options.',
    category: 'Billing'
  },
  {
    id: 6,
    question: 'Can I make changes after my project is completed?',
    answer: 'Absolutely! If you are on our Hosting & Management plan ($20/mo), minor text and image updates are included at no extra cost. For larger changes or new features, we provide a competitive hourly rate. We also offer revision rounds during the initial project phase to get everything just right.',
    category: 'Billing'
  },
  {
    id: 7,
    question: 'What kind of custom apps do you build?',
    answer: 'Our Custom App Development service ($5,000+) covers a wide range of web applications — from business dashboards and customer portals to booking systems and internal tools. We primarily work with modern technologies like React, Node.js, and Python. Each project is scoped individually based on your specific requirements.',
    category: 'Services'
  },
  {
    id: 8,
    question: 'Do I own my website after it is built?',
    answer: 'Yes, you own 100% of the website content and design we create for you. If you ever decide to move your hosting elsewhere, we will provide all necessary files and assist with the transition. The only exception is third-party licensed assets (stock photos, premium fonts) which maintain their original licensing terms.',
    category: 'General'
  },
  {
    id: 9,
    question: 'How do I get started?',
    answer: 'Getting started is easy! Simply reach out through our contact page or email us at nikos.local.web.design@gmail.com. We will schedule a free consultation to discuss your goals, timeline, and budget. From there, we will provide a detailed proposal and get to work once approved.',
    category: 'General'
  }
];

const FAQPage: React.FC = () => {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [faqList, setFaqList] = useState<FAQ[]>(staticFAQs);

  useEffect(() => {
    api.get('/faq')
      .then(res => {
        const data = res.data?.data || res.data?.faqs || res.data;
        if (Array.isArray(data) && data.length > 0) {
          const mapped: FAQ[] = data.map((f: any, i: number) => ({
            id: f.id || i + 1,
            question: f.question,
            answer: f.answer,
            category: f.category || 'General',
          }));
          setFaqList(mapped);
        }
      })
      .catch(() => {});
  }, []);

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const categories = ['All', ...Array.from(new Set(faqList.map(faq => faq.category)))];

  const filteredFAQs = selectedCategory === 'All' 
    ? faqList 
    : faqList.filter(faq => faq.category === selectedCategory);

  return (
    <>
      <Helmet>
        <title>FAQ - iModernize | Web Design & SEO Questions Answered</title>
        <meta name="description" content="Frequently asked questions about iModernize web design, SEO, hosting, and custom app development services. Learn about pricing, timelines, and what's included." />
        <meta name="keywords" content="web design FAQ, SEO services questions, website redesign cost, hosting management, custom app development" />
      </Helmet>

      <div className="py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Have questions? We've got answers. If you can't find what you're looking for, feel free to contact us.
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* FAQ Accordion */}
          <div className="max-w-3xl mx-auto space-y-4">
            {filteredFAQs.map((faq) => (
              <div
                key={faq.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => toggleExpand(faq.id)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    {faq.category !== 'General' && (
                      <span className="inline-block mt-2 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                        {faq.category}
                      </span>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    {expandedId === faq.id ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </div>
                </button>
                
                {expandedId === faq.id && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <p className="text-gray-700 whitespace-pre-line">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}

            {filteredFAQs.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">No FAQs found in this category.</p>
              </div>
            )}
          </div>

          {/* Contact CTA */}
          <div className="mt-16 text-center">
            <div className="bg-blue-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Still have questions?
              </h2>
              <p className="text-gray-600 mb-6">
                Can't find the answer you're looking for? We're here to help.
              </p>
              <a
                href="/contact"
                className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQPage;